import { useState, useCallback } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { BlockEditModal } from "../BlockEditModal";
import styles from "../blocks/blocks.module.scss";

export function VerseNodeView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const lines = (node.attrs.lines as string[]) || [];
  const attribution = (node.attrs.attribution as string) || "";
  const title = (node.attrs.title as string) || "";

  const [isOpen, setIsOpen] = useState(false);
  const [draftLines, setDraftLines] = useState(lines.join("\n"));
  const [draftAttribution, setDraftAttribution] = useState(attribution);
  const [draftTitle, setDraftTitle] = useState(title);

  const handleOpen = useCallback(() => {
    setDraftLines(lines.join("\n"));
    setDraftAttribution(attribution);
    setDraftTitle(title);
    setIsOpen(true);
  }, [lines, attribution, title]);

  const handleSave = useCallback(() => {
    updateAttributes({
      lines: draftLines.split("\n"),
      attribution: draftAttribution,
      title: draftTitle,
    });
    setIsOpen(false);
  }, [draftLines, draftAttribution, draftTitle, updateAttributes]);

  return (
    <NodeViewWrapper data-type="verse-block" data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
      <div
        className={styles.verseBlock}
        onClick={handleOpen}
        style={{ cursor: "pointer" }}
        title="Click to edit verse"
      >
        {title && <div className={styles.verseTitle}>{title}</div>}
        <div className={styles.verseContent}>
          {lines.length > 0 ? (
            lines.map((line, i) => (
              <div key={i} className={styles.verseLine}>{line || "\u00A0"}</div>
            ))
          ) : (
            <span style={{ color: "var(--lilia-text-muted, #999)" }}>Click to add verse...</span>
          )}
        </div>
        {attribution && (
          <div className={styles.verseAttribution}>&mdash; {attribution}</div>
        )}
      </div>

      <BlockEditModal
        sourceNode={node}
        title="Edit Verse"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSave}
        onDelete={() => { setIsOpen(false); deleteNode(); }}
        width={640}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Title (optional)</label>
          <input
            type="text"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            placeholder="e.g., Sonnet 18"
            style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }}
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Verse lines (one per line)</label>
          <textarea
            value={draftLines}
            onChange={(e) => setDraftLines(e.target.value)}
            placeholder="Enter verse lines..."
            rows={10}
            autoFocus
            style={{
              width: "100%", padding: "12px", fontSize: 13,
              border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4,
              resize: "vertical", lineHeight: 1.8, fontStyle: "italic",
            }}
          />
        </div>

        <div>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Attribution (optional)</label>
          <input
            type="text"
            value={draftAttribution}
            onChange={(e) => setDraftAttribution(e.target.value)}
            placeholder="e.g., William Shakespeare"
            style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }}
          />
        </div>
      </BlockEditModal>
    </NodeViewWrapper>
  );
}
